import { useId, useRef } from 'react';
import { ArtColoursProvider, lineOf, useArtColours, type ArtColourProps } from '../motion/art-colours';
import { AnimateTransform, Animate, SmilTimeline, usePlayOnceInView } from '../motion/smil';

/** The card's own colour, and the default paper: the envelope and the flag are filled with it so the lines behind them hide. */
const SAGE = '#EAEECF';

function useLine() {
	return lineOf(useArtColours().ink, 2);
}

const BODY =
	'M274.651 202.469L58.8343 246.265C50.0139 248.054 41.3447 242.579 39.1777 233.841L19.9227 156.312L19.1271 152.856C11.5097 119.767 29.4681 86.1315 61.201 74.0543L173.305 30.6598';
/** The hand-off breaks this outline where its envelope covered it; the last curve closes it again. */
const DOOR =
	'M295.747 163.027L298.98 177.072C301.418 187.662 294.655 198.177 284.011 200.358L233.359 210.727L166.754 223.819C157.616 225.616 148.662 219.986 146.318 210.974L122.868 120.839C114.216 83.2541 134.743 45.0768 170.866 31.572L175.413 29.8708C180.59 27.935 186.029 26.8015 191.546 26.5085C221.085 24.9339 248.391 38.614 265.068 61.2218C280 81.2 289 133.8 295.747 163.027';
const POST = ['M120.359 234.455L179.057 327.949', 'M203.664 326.362L207.751 216.045'];
/** Outline corners, then the flap, wound the same way: reversed, the flap cut a hole in the fill. */
const ENVELOPE: readonly (readonly [number, number])[] = [
	[147.095, 106.28],
	[296.363, 49.2956],
	[334.183, 148.367],
	[184.915, 205.352],
	[296.369, 49.2725],
	[246.667, 137.869],
	[147.105, 106.267],
];
/** Hinge at the foot of the bar, drawn standing up; the tab points to the box's back. */
const FLAG =
	'M-21 -72H3A3 3 0 0 1 6 -69V3A3 3 0 0 1 3 6H-3A3 3 0 0 1 -6 3V-56H-21A3 3 0 0 1 -24 -59V-69A3 3 0 0 1 -21 -72Z';
const CHECK = 'M-16.5 2L-6 12.5L16.5 -10';

/** The box leans 21 degrees; its axis, towards the front, is what the envelope folds and slides along. */
const TILT = 21;
const AXIS = { x: Math.cos((TILT * Math.PI) / 180), y: -Math.sin((TILT * Math.PI) / 180) };
/** The middle of the envelope's back edge, which leads it into the box. */
const LEAD = { x: 166, y: 155.8 };
const FLAG_HINGE = '78 140';
const BADGE = '250 45';

/** Right of the door's left edge and above its bottom edge, both inset half a stroke so the lip stays whole. */
const MOUTH = 'M79.6 -50H400V176.8L151.3 225.7Z';

type Pose = { at: number; fold: number; squash: number; back: number; ease?: 'in' | 'out' };
const REST: Pose = { at: 0.35, fold: 1, squash: 1, back: 0 };
/** `back` in artboard units: at 175 the whole card has crossed the mouth. */
const SLIDE: readonly Pose[] = [
	{ at: 0.8, fold: 0.92, squash: 0.97, back: 55, ease: 'in' },
	{ at: 1.15, fold: 0.8, squash: 0.94, back: 130 },
	{ at: 1.35, fold: 0.75, squash: 0.92, back: 175, ease: 'out' },
];
const GONE = SLIDE.at(-1) ?? REST;
const SECONDS = 3;

/** Moves the points rather than scaling the path, so the outline keeps its weight as it foreshortens. */
function envelopeAt(pose: Pose) {
	const [a, b, c, d, e, f, g] = ENVELOPE.map(([x, y]) => {
		const along = (x - LEAD.x) * AXIS.x + (y - LEAD.y) * AXIS.y;
		const across = (y - LEAD.y) * AXIS.x - (x - LEAD.x) * AXIS.y;
		const u = along * pose.fold - pose.back;
		const v = across * pose.squash;
		return `${(LEAD.x + u * AXIS.x - v * AXIS.y).toFixed(2)} ${(LEAD.y + u * AXIS.y + v * AXIS.x).toFixed(2)}`;
	});
	return `M${a}L${b}L${c}L${d}ZM${e}L${f}L${g}`;
}

function Envelope() {
	const line = useLine();
	const paper = useArtColours().paper;
	const clip = useId();
	return (
		<>
			<clipPath id={clip}>
				<path d={MOUTH} />
			</clipPath>
			<path d={envelopeAt(GONE)} clipPath={`url(#${clip})`} fill={paper} {...line}>
				<Animate
					attr="d"
					keys={[[REST.at, envelopeAt(REST)], ...SLIDE.map((pose) => [pose.at, envelopeAt(pose), pose.ease] as const)]}
				/>
			</path>
		</>
	);
}

function Flag() {
	const line = useLine();
	const paper = useArtColours().paper;
	return (
		<g transform={`translate(${FLAG_HINGE}) rotate(${-TILT})`}>
			<g>
				<AnimateTransform
					type="rotate"
					keys={[
						[GONE.at, -90],
						[1.8, 8, 'out'],
						[2, 0],
					]}
				/>
				<path d={FLAG} fill={paper} {...line} />
			</g>
		</g>
	);
}

function Badge() {
	const line = useLine();
	const accent = useArtColours().accent;
	return (
		<g transform={`translate(${BADGE})`}>
			<g>
				<AnimateTransform
					type="scale"
					keys={[
						[1.95, 0],
						[2.3, 1.15, 'out'],
						[2.5, 1],
					]}
				/>
				<circle r={32} fill={accent} />
				<path
					d={CHECK}
					fill="none"
					{...line}
					strokeWidth={2.8}
					pathLength={1}
					strokeDasharray="1 1"
					strokeDashoffset={0}
				>
					<Animate
						attr="stroke-dashoffset"
						keys={[
							[2.35, 1],
							[2.85, 0, 'out'],
						]}
					/>
				</path>
			</g>
		</g>
	);
}

/** Plays once in view and ends on the drawing served statically: the letter posted, the flag up, a check. */
export function MailboxIllustration(props: ArtColourProps & { label: string; className?: string }) {
	return (
		<ArtColoursProvider colours={props} paper={SAGE}>
			<MailboxSvg label={props.label} className={props.className} />
		</ArtColoursProvider>
	);
}

function MailboxSvg(props: { label: string; className?: string }) {
	const line = useLine();
	const ref = useRef<SVGSVGElement>(null);
	usePlayOnceInView(ref);
	return (
		<SmilTimeline seconds={SECONDS} once>
			<svg ref={ref} viewBox="0 0 370 358" fill="none" role="img" aria-label={props.label} className={props.className}>
				{POST.map((d) => (
					<path key={d} d={d} {...line} />
				))}
				<path d={BODY} {...line} />
				<path d={DOOR} {...line} />
				<Flag />
				<Envelope />
				<Badge />
			</svg>
		</SmilTimeline>
	);
}
